import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { ExternalLink, GitCompare } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { plural } from '../../utils/format';
import { useDeployStatus } from './useDeployStatus';
import { isDeployInProgress } from './useReleaseStatus';
import { DeployBadge, firstLine, shortSha, timeAgo } from './ui';
import { sitemapProblems } from './ReleaseBar';

/** Pages listed before "Show all": a shared header or footer change lists every page. */
const PAGE_LIMIT = 8;
/** GitHub's compare lists at most this many files. */
const GITHUB_FILE_LIMIT = 300;
const FILE_STATUS = { added: 'New', removed: 'Removed', renamed: 'Renamed' };

/** The shared header/footer editor's commits start "Shared header:" or "Shared footer:". */
const sharedScope = (commit) => /^Shared (header|footer)\b/i.exec(firstLine(commit.message))?.[1].toLowerCase();

/**
 * What a deploy ships, for review: pages, shared header and footer changes, and other files, with
 * a one-line summary. Without a file list (an older server), only the commits are known.
 */
const groupChanges = (status) => {
  const commits = status?.pendingCommits || [];
  const shared = { header: 0, footer: 0 };
  commits.forEach((c) => {
    const scope = sharedScope(c);
    if (scope) shared[scope] += 1;
  });
  const files = status?.changedFiles;
  if (!Array.isArray(files)) return { known: false, pages: [], others: [], shared };
  const pages = files.filter((f) => f.pageUrl).sort((a, b) => a.pageUrl.localeCompare(b.pageUrl));
  const others = files.filter((f) => !f.pageUrl);
  const summary = [
    pages.length > 0 && plural(pages.length, 'page'),
    shared.header > 0 && plural(shared.header, 'shared header change'),
    shared.footer > 0 && plural(shared.footer, 'shared footer change'),
    others.length > 0 && plural(others.length, 'other file'),
  ]
    .filter(Boolean)
    .join(', ');
  return { known: true, pages, others, shared, summary, capped: files.length >= GITHUB_FILE_LIMIT };
};

const FileCounts = ({ file }) => (
  <span className="mse-bulk-counts">
    <span className="text-success">+{file.additions}</span> <span className="text-danger">-{file.deletions}</span>
  </span>
);

const FileStatus = ({ file }) =>
  FILE_STATUS[file.status] ? (
    <Badge bg={file.status === 'removed' ? 'danger' : 'secondary'} className="ms-2">
      {FILE_STATUS[file.status]}
    </Badge>
  ) : null;

/** A Site health reference in a warning, as a button that opens that tab. */
const HealthLink = ({ tab, label, onOpenHealth }) => (
  <button type="button" className="mse-link-button" style={{ fontSize: 'inherit' }} onClick={() => onOpenHealth(tab)}>
    {label}
  </button>
);

/**
 * Confirms and runs Deploy to production: shows what will ship, page by page, then moves the prod
 * branch to the main commit shown here (the server refuses if main has moved since).
 */
const DeployProdModal = ({ show, status, sitemap, onClose, onDeployed, onRefresh, onOpenHealth }) => {
  const ensureToken = useAuthToken();
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [started, setStarted] = useState(null);
  // Freeze what the user reviews, so a background status refresh cannot change it under them.
  const [reviewed, setReviewed] = useState(null);
  const [reloading, setReloading] = useState(false);
  const deploy = useDeployStatus(started);
  const [links, setLinks] = useState({ state: 'checking' }); // checking | done (with report) | failed
  const [override, setOverride] = useState(false);
  const [allPages, setAllPages] = useState(false);
  const [showCommits, setShowCommits] = useState(false);
  const linkCheck = useRef(0);

  // The link check runs over main, which is what is about to ship. Only the latest check is shown.
  const checkLinks = useCallback(async () => {
    const run = ++linkCheck.current;
    setLinks({ state: 'checking' });
    try {
      await ensureToken();
      const report = (await marketingSiteService.siteLinks()).data;
      if (run === linkCheck.current) setLinks({ state: 'done', report });
    } catch {
      if (run === linkCheck.current) setLinks({ state: 'failed' });
    }
  }, [ensureToken]);

  const review = (next) => {
    setReviewed(next);
    setOverride(false);
    setAllPages(false);
    setShowCommits(!Array.isArray(next?.changedFiles));
  };

  useEffect(() => {
    if (show) {
      setError(null);
      setConflict(false);
      setStarted(null);
      review(status);
      checkLinks();
    }
    // Only when the dialog opens.
  }, [show]);

  const reloadList = async () => {
    setReloading(true);
    try {
      const fresh = await onRefresh();
      if (fresh) {
        review(fresh);
        setError(null);
        setConflict(false);
        checkLinks();
      }
    } finally {
      setReloading(false);
    }
  };

  const confirm = async () => {
    setDeploying(true);
    setError(null);
    setConflict(false);
    try {
      await ensureToken();
      const response = await marketingSiteService.deployProd(reviewed.mainHead.sha);
      setStarted(response.data);
      onDeployed(response.data);
    } catch (err) {
      setConflict(err?.response?.status === 409);
      setError(apiErrorMessage(err, 'Deploy failed to start.'));
    } finally {
      setDeploying(false);
    }
  };

  const commits = reviewed?.pendingCommits || [];
  const changes = groupChanges(reviewed);
  const shownPages = allPages ? changes.pages : changes.pages.slice(0, PAGE_LIMIT);
  const siteUrl = (reviewed?.siteUrl || '').replace(/\/$/, '');
  const compareUrl =
    reviewed?.compareUrl ||
    (commits[0]?.url && reviewed?.prodBranch && reviewed?.mainBranch
      ? commits[0].url.replace(/\/commit\/[^/]+$/, `/compare/${reviewed.prodBranch}...${reviewed.mainBranch}`)
      : null);

  const testDeploy = reviewed?.testDeploy;
  const testFailed = testDeploy?.status === 'DEPLOY_FAILED';
  const testRunning = isDeployInProgress(testDeploy);
  const testUnknown = testDeploy && !testFailed && !testRunning && testDeploy.status !== 'DEPLOYED';
  const broken = links.state === 'done' ? links.report?.counts?.ERROR || 0 : 0;
  const needsOverride = testFailed || broken > 0;
  // A background refresh shows main has moved on since the list was frozen
  const mainMoved = !started && !!reviewed?.mainHead && !!status?.mainHead && status.mainHead.sha !== reviewed.mainHead.sha;
  const finished = deploy?.status === 'DEPLOYED';

  return (
    <Modal show={show} onHide={deploying ? undefined : onClose} size="lg" fullscreen="sm-down">
      <Modal.Header closeButton={!deploying}>
        <Modal.Title>{started ? (finished ? 'Deployed to production' : 'Deploying to production') : 'Deploy to production'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {started ? (
          <div>
            <div className="d-flex align-items-center gap-2 mb-3">
              Production deploy of <code>{shortSha(started.commitSha)}</code>: <DeployBadge deploy={deploy} />
              {isDeployInProgress(deploy) && <Spinner animation="border" size="sm" />}
            </div>
            {deploy?.detail && <p className="mse-hint">{deploy.detail}</p>}
            {deploy?.pipelineUrl && (
              <a href={deploy.pipelineUrl} target="_blank" rel="noopener noreferrer">
                View the CircleCI pipeline
              </a>
            )}
          </div>
        ) : (
          <>
            <p className="mb-2">
              This publishes everything on the test site that is not yet in production to{' '}
              <strong>{reviewed?.prodUrl?.replace(/^https?:\/\//, '')}</strong>.
            </p>
            <div className="d-flex flex-wrap align-items-baseline gap-2 mb-3">
              <strong>{changes.known ? changes.summary || 'No file changes' : plural(commits.length, 'change')}</strong>
              {changes.known && <span className="mse-hint">in {plural(commits.length, 'commit')}</span>}
              {compareUrl && (
                <a href={compareUrl} target="_blank" rel="noopener noreferrer" className="ms-auto small d-inline-flex align-items-center gap-1">
                  <GitCompare size={13} /> Compare on GitHub
                </a>
              )}
            </div>

            {mainMoved && (
              <Alert variant="info" className="d-flex flex-wrap align-items-center gap-2">
                <span>The test site has new commits since this list was loaded.</span>
                <Button size="sm" variant="outline-secondary" onClick={reloadList} disabled={reloading}>
                  {reloading ? 'Reloading' : 'Reload the list'}
                </Button>
              </Alert>
            )}

            {changes.pages.length > 0 && (
              <>
                <div className="mse-sublabel mb-1">Pages</div>
                {changes.shared.header + changes.shared.footer > 0 && (
                  <p className="mse-hint mb-1">A shared header or footer change lists every page that has it.</p>
                )}
                <ul className="mse-commit-list mb-1">
                  {shownPages.map((f) => (
                    <li key={f.path}>
                      <span className="mse-commit-message">
                        <code>{f.pageUrl}</code>
                        <FileStatus file={f} />
                      </span>
                      <FileCounts file={f} />
                      {f.status !== 'removed' && siteUrl && (
                        <a className="small text-nowrap" href={`${siteUrl}${f.pageUrl}`} target="_blank" rel="noopener noreferrer">
                          Open on test site
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
                {changes.pages.length > PAGE_LIMIT && (
                  <button type="button" className="mse-link-button mb-2" onClick={() => setAllPages((v) => !v)}>
                    {allPages ? 'Show fewer pages' : `Show all ${changes.pages.length} pages`}
                  </button>
                )}
              </>
            )}
            {changes.others.length > 0 && (
              <>
                <div className="mse-sublabel mb-1 mt-2">Other files</div>
                <ul className="mse-commit-list mb-1">
                  {changes.others.map((f) => (
                    <li key={f.path}>
                      <span className="mse-commit-message">
                        <code>{f.path}</code>
                        <FileStatus file={f} />
                      </span>
                      <FileCounts file={f} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {changes.capped && (
              <p className="mse-hint">GitHub lists the first {GITHUB_FILE_LIMIT} files. Compare on GitHub shows them all.</p>
            )}

            {changes.known && (
              <button type="button" className="mse-link-button mt-2 mb-2" onClick={() => setShowCommits((v) => !v)} aria-expanded={showCommits}>
                {showCommits ? 'Hide' : 'Show'} the {plural(commits.length, 'commit')}
              </button>
            )}
            {showCommits && (
              <ul className="mse-commit-list">
                {commits.map((c) => (
                  <li key={c.sha}>
                    <a href={c.url} target="_blank" rel="noopener noreferrer">
                      <code>{shortSha(c.sha)}</code>
                    </a>
                    <span className="mse-commit-message">{firstLine(c.message)}</span>
                    <span className="mse-commit-meta">
                      {c.authorName} {timeAgo(c.date)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3">
              {links.state === 'checking' && (
                <div className="d-flex align-items-center gap-2 text-muted small mb-3">
                  <Spinner animation="border" size="sm" /> Checking links on the test site
                </div>
              )}
              {links.state === 'failed' && (
                <p className="mse-hint">
                  The link check did not finish. Check{' '}
                  <HealthLink tab="links" label="Site health, Links" onOpenHealth={onOpenHealth} /> before deploying.
                </p>
              )}
              {testFailed && (
                <Alert variant="danger">
                  The latest test site deploy failed, so the test site may not show these changes. Check the test site
                  before publishing.
                  {testDeploy.pipelineUrl && (
                    <>
                      {' '}
                      <a href={testDeploy.pipelineUrl} target="_blank" rel="noopener noreferrer">
                        View the CircleCI pipeline
                      </a>
                    </>
                  )}
                </Alert>
              )}
              {testRunning && (
                <Alert variant="warning">
                  The test site is still deploying (<DeployBadge deploy={testDeploy} />), so it may not show the latest
                  changes yet. Check it once the deploy finishes.
                </Alert>
              )}
              {testUnknown && (
                <p className="mse-hint">The test site deploy status is unknown. Check the test site before publishing.</p>
              )}
              {broken > 0 && (
                <Alert variant="warning">
                  The test site has {plural(broken, 'broken link')} or missing file{broken === 1 ? '' : 's'}. See{' '}
                  <HealthLink tab="links" label="Site health, Links" onOpenHealth={onOpenHealth} />.
                </Alert>
              )}
              {sitemapProblems(sitemap) > 0 && (
                <Alert variant="warning">
                  sitemap.xml is out of date ({sitemap.missing.length} missing, {sitemap.stale.length} to remove). Search
                  engines find new pages sooner once it is updated:{' '}
                  <HealthLink tab="index" label="Site health, Sitemap and llms.txt" onOpenHealth={onOpenHealth} />.
                </Alert>
              )}
              {needsOverride && (
                <Form.Check
                  type="checkbox"
                  id="deploy-anyway"
                  className="mb-2"
                  label="Deploy anyway"
                  checked={override}
                  onChange={(e) => setOverride(e.target.checked)}
                  disabled={deploying}
                />
              )}
              {error && (
                <Alert variant={conflict ? 'warning' : 'danger'} className="mb-0">
                  {error}
                  {conflict && (
                    <div className="mt-2">
                      <Button size="sm" variant="outline-secondary" onClick={reloadList} disabled={reloading}>
                        {reloading ? 'Reloading' : 'Reload the list'}
                      </Button>
                    </div>
                  )}
                </Alert>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {started ? (
          <>
            <Button variant="outline-secondary" href={reviewed?.prodUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} className="me-1" />
              Open production site
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={deploying}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirm}
              disabled={deploying || reloading || mainMoved || !reviewed?.canDeploy || (needsOverride && !override)}
              title={needsOverride && !override ? 'Tick Deploy anyway to publish despite the warnings' : undefined}
            >
              {deploying ? 'Starting deploy' : 'Deploy to production'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeployProdModal;
